import React, { useState, useEffect } from "react";
import { Paper, Typography, Grid2, Divider } from "@mui/material";
import { useForm } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import ControlledTextField from "@components/inputs/controlledTextField";

import { useSnackbar } from "@context/snackbarContext";

import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";

import { formatDateTime } from "@utils/formatter";


const schema = z.object({
  emailAddress: z.string().email("Invalid email address"),

  /* view only */
  createdOn: z.string().optional(),
  createdBy: z.string().optional(),
});

export default function SubscriberForm() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const navigate = useNavigate()
  const { pathname } = useLocation();
  const { user } = useAuthentication();
  const pageAction = pathname.split("/")[3] || null; // get from pathname expected values ["create-subscriber" || "view-subscriber" || "edit-subscriber"]
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  // console.log(user)

  const { handleSubmit, control, getValues, reset } = useForm({
    resolver: zodResolver(schema)
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  useEffect(()=>{
    if (((pageAction === "edit-subscriber" || pageAction === "view-subscriber") && !id) || (pageAction === "create-subscriber" && id)) {
      onBack()
    }
    if (id) {
      fetchSubscriberMutation.mutate(id)
    }
  },[pageAction])

  const fetchSubscriberMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${AdminApiEndpoints.getSubscriber}/${id}`)
    },
    onSuccess: (res) => {
      const {createdOn, ...data} = res.data
      const createdOnFormatted = formatDateTime(createdOn)
      reset({...data, createdOn: createdOnFormatted})
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve subscriber", "error");
    }
  });

  const createSubscriberMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(AdminApiEndpoints.createSubscriber, {...data, by: user.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getSubscriber'] })
      openSnackbar("Successfully created subscriber", "success");
      onBack()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to create subscriber", "error");
    }
  })

  const updateSubscriberMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.updateSubscriber}/${id}`, {...data, by: user.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getSubscriber'] })
      openSnackbar("Successfully updated subscriber", "success");
      fetchSubscriberMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to update subscriber", "error");
    }
  })

  const onSubmit = () => {
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    const data = getValues();
    // console.log(data)
    // return
    if(pageAction === "create-subscriber")
      createSubscriberMutation.mutate(data)
    else
      updateSubscriberMutation.mutate(data)
  }

  const onBack = () => {
    navigate('/admin/broadcasts?tab=subscriber', { replace: true });
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs />
      <SkeletonWrapper isLoading={fetchSubscriberMutation.isPending} isError={fetchSubscriberMutation.isError} errorMessage="Unable to load subscriber" disabled={["create-subscriber"].includes(pageAction)}>
      <form onSubmit={handleSubmit(onSubmit)} style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Subscriber Detail</Typography>
            </Grid2>

            <Grid2 size={12}>
              <ControlledTextField 
                name="emailAddress"
                control={control}
                label="Email Address"
                disabled={fetchSubscriberMutation.isPending || fetchSubscriberMutation.isError}
                readOnly={["view-subscriber"].includes(pageAction)}
              />
            </Grid2>

            {["view-subscriber", "edit-subscriber"].includes(pageAction) && <>

            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdOn"
                control={control}
                label="Created On"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdBy"
                control={control}
                label="Created By"
                disabled={true}
              />
            </Grid2>
            
            </>}

          </Grid2>
        </Paper>
        {["create-subscriber", "edit-subscriber"].includes(pageAction) && 
          <Paper
            sx={{
              p: "20px 25px",
              boxShadow: "none",
              borderRadius: "10px",
              border: "1px solid #E5E5E5",
              display: "flex",
              justifyContent: 'flex-end',
              gap: "10px"
            }}
          >
            <CustomButton type="submit" disabled={fetchSubscriberMutation.isPending || fetchSubscriberMutation.isError}>Submit</CustomButton>
          </Paper>
        }
      </form>
      </SkeletonWrapper>
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription="Submit subscriber?"
        onSubmit={()=>onConfirmSubmit()}
        loading={createSubscriberMutation.isPending || updateSubscriberMutation.isPending}
      />
    </div>
  );
}
